import React, { useState } from 'react';
import { fetchWhatsappChats } from '../api';

/**
 * Lists the linked WhatsApp account's chats so the admin can copy a group's
 * serialized ID (…@g.us) into WHATSAPP_GROUP_ID. Loaded on demand — getChats()
 * can be slow on large accounts. Defaults to groups only, with a name/ID filter.
 */
export default function WhatsAppChats({ onAuthError }) {
  const [chats, setChats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [groupsOnly, setGroupsOnly] = useState(true);
  const [copied, setCopied] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchWhatsappChats();
      setChats(res.chats || []);
    } catch (err) {
      if (err.message === 'AUTH') {
        onAuthError && onAuthError();
        return;
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copy = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(id);
      setTimeout(() => setCopied(''), 1500);
    } catch {
      setError('Could not copy — select the ID and copy manually.');
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = (chats || [])
    .filter((c) => (groupsOnly ? c.isGroup : true))
    .filter((c) => !q || c.name.toLowerCase().includes(q) || c.id.toLowerCase().includes(q));

  return (
    <div className="wa-chats">
      <button className="btn secondary" onClick={load} disabled={loading}>
        {loading ? 'Loading…' : chats ? 'Refresh chats' : 'Find group ID'}
      </button>

      {error && <div className="status-bar error">{error}</div>}

      {chats && (
        <>
          <div className="wa-chats-controls">
            <input
              type="text"
              placeholder="Filter by name or ID…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <label className="wa-inline">
              <input
                type="checkbox"
                checked={groupsOnly}
                onChange={(e) => setGroupsOnly(e.target.checked)}
              />
              Groups only
            </label>
          </div>

          <ul className="wa-chat-list">
            {filtered.length === 0 && <li className="hint">No matching chats.</li>}
            {filtered.map((c) => (
              <li key={c.id} className="wa-chat-item">
                <span className="wa-chat-name" title={c.name}>
                  {c.name || '(no name)'}
                  {c.isGroup ? ' · group' : ''}
                </span>
                <code className="wa-chat-id">{c.id}</code>
                <button className="btn-link" onClick={() => copy(c.id)}>
                  {copied === c.id ? 'Copied!' : 'Copy'}
                </button>
              </li>
            ))}
          </ul>

          <p className="hint">
            Copy a group's ID (ends with <code>@g.us</code>) into{' '}
            <code>WHATSAPP_GROUP_ID</code>.
          </p>
        </>
      )}
    </div>
  );
}
